import { ComponentProps, ReactNode } from 'react';

import Button from '~/components/scales/Button/Button';
import Dialog from '~/components/scales/Dialog/Dialog';
import { useDialogFlow } from '~/components/scales/Dialog/DialogUtils';

export type RenderArgs = {
  index: number;
  onBack: () => void;
  onNext: () => void;
  progressPercent: number;
};

export type DialogFlowStep = {
  title?: ReactNode;
  renderContent: (args: RenderArgs) => ReactNode;
  renderFooterRight?: (args: RenderArgs) => ReactNode;
  renderFooterLeft?: (args: RenderArgs) => ReactNode;
  renderFooterCenter?: (args: RenderArgs) => ReactNode;
};

type Props = {
  accentColor?: string;
  isFullHeight?: boolean;
  isOpen: boolean;
  onClose: (step: number) => void;
  onComplete: () => void;
  size?: ComponentProps<typeof Dialog>['size'];
  steps: DialogFlowStep[];
  title: ComponentProps<typeof Dialog>['title'];
  ['data-cy']?: ComponentProps<typeof Dialog>['data-cy'];
};

export default function DialogFlow(props: Props) {
  const { index, onBack, onNext, progressPercent } = useDialogFlow(
    props.steps.length,
    props.onComplete,
    props.onClose
  );

  const backButton = (
    <Button
      data-cy="dialog-back-button"
      label={index === 0 ? 'Close' : 'Back'}
      onClick={onBack}
      type="secondary"
    />
  );

  const forwardButton = (
    <Button
      data-cy="dialog-next-button"
      label={index === props.steps.length - 1 ? 'Done' : 'Next'}
      onClick={onNext}
      type="primary"
    />
  );

  // Rendering
  const currentStep = props.steps[index];
  const renderArgs = { index, progressPercent, onNext, onBack };

  return (
    <Dialog
      accentColor={props.accentColor}
      data-cy={props['data-cy'] || 'progress-dialog'}
      footerCenter={
        currentStep.renderFooterCenter ? currentStep.renderFooterCenter(renderArgs) : undefined
      }
      footerLeft={
        currentStep.renderFooterLeft ? currentStep.renderFooterLeft(renderArgs) : backButton
      }
      footerRight={
        currentStep.renderFooterRight ? currentStep.renderFooterRight(renderArgs) : forwardButton
      }
      isFullHeight={props.isFullHeight}
      isOpen={props.isOpen}
      onClose={() => props.onClose(index)}
      progressPercent={progressPercent}
      size={props.size}
      title={props.title}
    >
      {currentStep.renderContent(renderArgs)}
    </Dialog>
  );
}
